import { Box, BoxProps, useBreakpointValue } from "@chakra-ui/react";
import { MutableRefObject } from "react";
import { ColouredTickList } from "../components/ColouredTickList";
import { GoogleMapsEmbed } from "../components/GoogleMapsEmbed";
import { CaravanList } from "../components/ParkPageComponents";
import { PlayVideo } from "../components/PlayVideo";
import { PARAGRAPH_SPACING } from "../const";
import { DEFAULT_FIRST_FORM_VALUE, useFilterStore } from "../stores/FilterStore";
import { decodeHtmlEntities } from "../utils";
import { useFilteredCaravanStock } from "./useFilteredCaravanStock";
import { useGetParkDetails } from "./useGetParkDetails";
import { useGetParkStockData } from "./useGetParkStockData";

export const useParkPageTabData = (videoRef: MutableRefObject<null>) => {
  const { data: caravanStock } = useGetParkStockData();
  const { filteredCaravanStock } = useFilteredCaravanStock();

  const isMobile = useBreakpointValue({ base: true, md: false });

  const { data: parkDetails } = useGetParkDetails();
  const { bedrooms, minPrice, maxPrice } = useFilterStore(["bedrooms", "minPrice", "maxPrice"]);

  if (!parkDetails) return { tabData: [] };

  const { facilities, vid, attractions, facilOverview, directions, name, sAdd, lat, lon, overview } = parkDetails;

  const invalidSearchValues = ["any", DEFAULT_FIRST_FORM_VALUE];
  const hasDoneASearch =
    !invalidSearchValues.includes(bedrooms) ||
    !invalidSearchValues.includes(minPrice) ||
    !invalidSearchValues.includes(maxPrice);

  const showSearchTab = filteredCaravanStock.length >= 1 && hasDoneASearch;

  const hasFacilities = Boolean(facilities) && facilities.length > 0;
  const hasFacilitiesOverview = Boolean(facilOverview) && facilOverview.length > 0;
  const showFacilitiesTab = hasFacilities || hasFacilitiesOverview;

  const hasMapCoords = Number(lat) !== 0 && Number(lon) !== 0;
  const hasDirections = Boolean(directions?.trim());
  const showLocationTab = hasMapCoords || hasDirections;
  const showVideoTab = Boolean(vid);
  const showAttractionsTab = Boolean(attractions);

  const tabData = [
    {
      condition: overview?.length > 0 && isMobile,
      tabTitle: "Overview",
      showInCard: true,
      content: <DecodedText textData={overview} />,
    },
    {
      condition: showSearchTab,
      tabTitle: "Search Results",
      showInCard: false,
      content: <CaravanList caravanStock={filteredCaravanStock} />,
    },
    {
      condition: true,
      tabTitle: "All Holiday Homes",
      showInCard: false,
      content: <CaravanList caravanStock={caravanStock || []} />,
    },
    {
      condition: showFacilitiesTab,
      tabTitle: "Facilities",
      showInCard: true,
      content: (
        <>
          {hasFacilitiesOverview && <DecodedText pb={hasFacilities ? 4 : 0} textData={facilOverview} />}
          {hasFacilities && <ColouredTickList data={facilities} columns={2} />}
        </>
      ),
    },
    {
      condition: showLocationTab,
      tabTitle: "Location",
      showInCard: true,
      content: (
        <>
          {hasMapCoords && <GoogleMapsEmbed name={name} location={sAdd} lat={Number(lat)} lon={Number(lon)} />}
          {hasDirections && <DecodedText pt={hasMapCoords ? 4 : 0} textData={directions} />}
        </>
      ),
    },
    {
      condition: showVideoTab,
      tabTitle: "Video",
      showInCard: true,
      content: <PlayVideo m={0} src={vid} ref={videoRef} />,
    },
    {
      condition: showAttractionsTab,
      tabTitle: "Attractions",
      showInCard: true,
      content: <DecodedText textData={attractions} />,
    },
  ];

  return { tabData };
};

const DecodedText: React.FC<{ textData: string } & BoxProps> = ({ textData, ...rest }) => (
  <Box
    sx={PARAGRAPH_SPACING}
    dangerouslySetInnerHTML={{
      __html: decodeHtmlEntities(textData),
    }}
    {...rest}
  />
);
